/**
 * Sales insights read API (assayer-backend `api/insights.py`, SALES_INSIGHTS_PLAN.md).
 *
 * Six `sales`-role endpoints. Numbers arrive as JSON numbers (the backend
 * converts NUMERIC to float before responding). Every endpoint also answers
 * `format=csv`, which `downloadInsightsCsv` fetches as a blob.
 */
import { apiClient } from './config.js';
import { waitForMaintenanceJob, type MaintenanceJobStatus } from './tools.js';

export interface Pipeline {
  is_customer: boolean;
  is_prospect: boolean;
  campaigns: string[];
}

/** Recency band (SALES_INSIGHTS_PLAN.md D14): hot ≤ 91 days, warm ≤ 365, cold beyond. */
export type RecencyBand = 'hot' | 'warm' | 'cold';

/** A competitor customer (M6). `observed` = "seen by" the source date (D15). */
export interface CompetitorSignal {
  competitor: string;
  last_seen: string | null;
  first_seen: string | null;
  date_basis: 'dated' | 'observed';
  pieces: number | null;
  band: RecencyBand | null;
  label?: string;
}

/** One IR/PR firm seen acting for a company, past ones included, newest first. */
export interface FirmLink {
  firm_id: string;
  name: string;
  first_seen: string | null;
  last_seen: string | null;
  current: boolean;
  band: RecencyBand | null;
  label?: string;
}

export interface FirmSummary {
  status?: 'firm' | 'none_observed' | 'unlinked';
  current_firms: string[];
  firm_mix: 'concurrent' | 'likely_switch' | null;
  firm_links?: FirmLink[];
}

export interface Reason {
  key: 'runway_low' | 'high_coverage' | 'social_reach' | 'firm_switch' | 'competitor';
  label: string;
}

export interface CoverageItem {
  entity_id: string;
  cynapse_entity_id: string | null;
  ticker: string | null;
  name: string | null;
  articles_total: number;
  articles_window: number;
  first_published: string | null;
  last_published: string | null;
  market_cap: number | null;
  market_cap_date: string | null;
  linkedin_followers: number | null;
  social: { posts: number; impressions: number; engagements: number; impressions_per_article: number | null };
  runway: { quarters: number | null; period_end: string | null; form: string | null; low: boolean };
  pipeline: Pipeline;
  firms?: FirmSummary;
  competitors?: CompetitorSignal[];
  reasons?: Reason[];
}

export interface CoverageResponse {
  window_days: number;
  max_market_cap: number | null;
  count: number;
  items: CoverageItem[];
  firm_note: string | null;
}

export interface HotListResponse extends CoverageResponse {
  median_impressions_per_article: number | null;
}

export interface EngagementArticle {
  article_id: string;
  title: string | null;
  published_at: string | null;
  slug: string | null;
  entity_id: string | null;
  ticker: string | null;
  name: string | null;
  mastheads: string[];
  platforms: string[];
  posts: number;
  impressions: number;
  engagements: number;
  clicks: number | null;
  multiple_of_median: number | null;
}

export interface EngagementEntity {
  entity_id: string;
  ticker: string | null;
  name: string | null;
  articles: number;
  posts: number;
  impressions: number;
  engagements: number;
  clicks: number | null;
  multiple_of_median: number | null;
}

export interface Firm {
  firm_id: string;
  name: string | null;
  domain: string | null;
  clients_current: number;
  clients_all: number;
  covered_current: number;
  articles_window: number;
  articles_total: number;
  customers: number;
  in_pipeline: number;
  multi_firm_clients: number;
  likely_switches: number;
}

export interface FirmClient {
  entity_id: string;
  ticker: string | null;
  name: string | null;
  first_seen: string | null;
  last_seen: string | null;
  current: boolean;
  articles_total: number;
  articles_window: number;
  last_published: string | null;
  pipeline: Pipeline;
  other_firms: string[];
  other_firm_links?: FirmLink[];
  competitors?: CompetitorSignal[];
  firm_mix: 'concurrent' | 'likely_switch' | null;
}

export interface FirmsResponse {
  window_days: number;
  caveat: string;
  covered_companies_linked: number;
  covered_companies_no_firm_observed: number;
  count: number;
  items: Firm[];
}

export interface FirmDetail extends Firm {
  window_days: number;
  caveat: string;
  clients: FirmClient[];
}

/** `null` means no market-cap band (the backend's `no_cap=true`). */
export type CapBand = number | null;

function capParams(maxMarketCap: CapBand) {
  return maxMarketCap === null ? { no_cap: true } : { max_market_cap: maxMarketCap };
}

export async function getHotList(maxMarketCap: CapBand, limit = 100): Promise<HotListResponse> {
  const { data } = await apiClient.get<HotListResponse>('/insights/hot-list', {
    params: { ...capParams(maxMarketCap), limit },
  });
  return data;
}

export async function getCoverage(
  windowDays: number, maxMarketCap: CapBand, includeCustomers: boolean, limit = 50,
): Promise<CoverageResponse> {
  const { data } = await apiClient.get<CoverageResponse>('/insights/coverage', {
    params: { window_days: windowDays, include_customers: includeCustomers, limit, ...capParams(maxMarketCap) },
  });
  return data;
}

export async function getFirms(windowDays: number, limit = 100): Promise<FirmsResponse> {
  const { data } = await apiClient.get<FirmsResponse>('/insights/firms', {
    params: { window_days: windowDays, limit },
  });
  return data;
}

export async function getFirm(firmId: string, windowDays: number): Promise<FirmDetail> {
  const { data } = await apiClient.get<FirmDetail>(`/insights/firms/${firmId}`, {
    params: { window_days: windowDays },
  });
  return data;
}

export async function getEngagementArticles(windowDays: number, limit = 50) {
  const { data } = await apiClient.get<{ window_days: number; measurement_note: string; items: EngagementArticle[] }>(
    '/insights/engagement/articles', { params: { window_days: windowDays, limit } },
  );
  return data;
}

export async function getEngagementEntities(windowDays: number, limit = 50) {
  const { data } = await apiClient.get<{ window_days: number; items: EngagementEntity[] }>(
    '/insights/engagement/entities', { params: { window_days: windowDays, limit } },
  );
  return data;
}

/** Any insights endpoint as CSV; `path` is relative to `/insights`. */
export async function downloadInsightsCsv(path: string, params: Record<string, unknown>): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/insights/${path}`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });
  return data;
}

// --- Find and share (backend 0.3.5) ------------------------------------------

export interface InsightsSearchResult {
  q: string;
  companies: { entity_id: string; ticker: string | null; name: string | null; articles: number }[];
  firms: { firm_id: string; name: string | null; domain: string | null; clients_current: number }[];
  /** True when nothing matched exactly and these are trigram/near-name suggestions. */
  fuzzy?: boolean;
}

export interface Story {
  story_id: string;
  entity_id: string;
  ticker: string | null;
  name: string | null;
  published_at: string | null;
  /** The masthead whose copy is the story's primary (index/social owner). */
  owner: string;
  title: string | null;
  /** Live link to share: the owner's copy when live, else the newest live copy. */
  url: string | null;
  masthead: string | null;
  also_on: { masthead: string | null; url: string }[];
  /** Social, summed across every masthead copy's posts (one-shot snapshot 12h–4d after posting). */
  impressions: number;
  social_clicks?: number;
  reach?: number;
  engagements?: number;
  /** Human page views (complete when views_exact) and Google clicks (top-100 days only — always a floor). */
  views?: number;
  /** Every live copy went live after its site's page views were counted in full. */
  views_exact?: boolean;
  search_clicks?: number;
  /** Times an AI assistant fetched our copies because a person asked it something (since 6 Sep 2026). */
  ai_reads?: number;
  /** Some of those days were answered from a Cloudflare sample. */
  ai_reads_estimated?: boolean;
  copies?: number;
}

export interface SocialByPlatform {
  platform: string;
  posts: number;
  not_available: number;
  pending: number;
  impressions: number | null;
  reach: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  reposts: number | null;
  clicks: number | null;
  as_at: string | null;
}

export interface Readership {
  views: number;
  stories_with_views: number;
  search_clicks: number;
  since: string;
  floor: boolean;
  ai_reads?: number;
  stories_with_ai_reads?: number;
  ai_reads_estimated?: boolean;
  ai_since?: string;
  note: string;
}

export interface CompanyHub {
  masthead: string;
  url: string;
  /** True when this is the owner masthead's hub (Resources → Orecast, else Market Floor). */
  owner: boolean;
}

export interface CompanyArticles {
  entity_id: string;
  ticker: string | null;
  name: string | null;
  market_cap: number | null;
  /** The company's hub page to link, or null when no masthead has one. */
  hub?: CompanyHub | null;
  pipeline: Pipeline;
  firms: FirmLink[];
  firm_mix?: 'concurrent' | 'likely_switch' | null;
  competitors: CompetitorSignal[];
  coverage?: { articles_total: number; articles_90d: number; first_published: string | null; last_published: string | null; mastheads: string[] };
  runway?: { quarters: number | null; period_end: string | null; form: string | null; low: boolean };
  social?: SocialByPlatform[];
  readership?: Readership;
  reasons?: Reason[];
  window_days: number;
  stories: Story[];
}

export interface FirmArticles {
  firm_id: string;
  name: string | null;
  domain: string | null;
  window_days: number;
  caveat: string;
  clients: {
    entity_id: string; ticker: string | null; name: string | null; stories: number; pipeline: Pipeline;
    competitors: CompetitorSignal[]; other_firm_links: FirmLink[];
  }[];
  past_clients: { entity_id: string; ticker: string | null; name: string | null; last_seen: string | null }[];
  stories: Story[];
}

export async function searchInsights(q: string): Promise<InsightsSearchResult> {
  const { data } = await apiClient.get<InsightsSearchResult>('/insights/search', { params: { q } });
  return data;
}

export async function getCompanyArticles(entityId: string, windowDays: number): Promise<CompanyArticles> {
  const { data } = await apiClient.get<CompanyArticles>(`/insights/companies/${entityId}/articles`, {
    params: { window_days: windowDays },
  });
  return data;
}

export async function getFirmArticles(firmId: string, windowDays: number): Promise<FirmArticles> {
  const { data } = await apiClient.get<FirmArticles>(`/insights/firms/${firmId}/articles`, {
    params: { window_days: windowDays },
  });
  return data;
}

/** One competitor-customer row for the M6 import (SALES_INSIGHTS_PLAN.md §5b). */
export interface CompetitorImportRow {
  competitor: string;
  source: string;
  date_basis: 'dated' | 'observed';
  last_seen: string;
  first_seen?: string | null;
  name?: string | null;
  ticker?: string | null;
  pieces?: number | null;
}

export interface CompetitorImportResult {
  dry_run: boolean;
  floor: number;
  sources: string[];
  rows: number;
  matched_rows: number;
  by_method: Record<string, number>;
  signals: number;
  companies: number;
  ambiguous: { name: string; candidates: string[] }[];
  unmatched: string[];
  headline_matches: { name: string; company: string | null; score: number; votes: number }[];
  name_lookup?: string;
  headline_calibration?: { sample: number; by_floor: Record<string, { agree: number; wrong: number; no_vote: number; precision: number | null }> };
  deleted?: number;
  inserted?: number;
}

/** Durable job: match rows to companies; apply replaces each source's rows. */
export async function runCompetitorImport(
  rows: CompetitorImportRow[], params: { dry_run: boolean; floor?: number },
  onProgress?: (s: MaintenanceJobStatus) => void,
): Promise<CompetitorImportResult> {
  const { data } = await apiClient.post<{ job_id: string }>(
    // passages: match headlines to filing opening passages too (fast since mig 406)
    '/tools/competitor-signals-import', { rows, floor: params.floor ?? null, passages: true }, { params: { dry_run: params.dry_run } },
  );
  return waitForMaintenanceJob<CompetitorImportResult>(data.job_id, onProgress);
}

export interface CompetitorSweepSite {
  discover: { sitemaps?: number; urls?: number; unfetched?: number; error?: string };
  fetch: { queued: number; fetched: number; paid: number; failed: number };
  pages_fetched_total: number;
  paid_total: number;
  alarm?: string;
  signal_rows: number;
  import?: { companies: number; signals: number; matched_rows: number; rows: number; inserted?: number; deleted?: number };
  unbound_tickers?: string[];
}

export interface CompetitorSweepResult {
  dry_run: boolean;
  max_pages: number;
  sites: Record<string, CompetitorSweepSite>;
}

/** Durable job: competitor paid-coverage sweep (M7). Runs weekly on its own; this is the manual trigger. */
export async function runCompetitorSweep(
  params: { dry_run: boolean; max_pages: number },
  onProgress?: (s: MaintenanceJobStatus) => void,
): Promise<CompetitorSweepResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/competitor-sweep', null, { params });
  return waitForMaintenanceJob<CompetitorSweepResult>(data.job_id, onProgress);
}
